// ConvertElementsCountsTask.js
// Zadanie 2: Dane JEDNE z (s, k, w). Oblicz INNE (losowo jedno z pozostałych dwóch).
// Oznaczenia (spolszczone): s – ściany, k – krawędzie, w – wierzchołki
// Zależności: s = n+1, w = n+1, k = 2n  ⇒  n = s-1 = w-1 = k/2.
// Wyjaśnienie liczymy "przez n": najpierw ustalamy n, potem szukaną wielkość.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function buildFromN(n) {
  return { s: n + 1, w: n + 1, k: 2 * n };
}

function parseIntAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return null;
  const s = String(userAnswer.raw).trim();
  if (!/^-?\d+$/.test(s)) return null;
  return parseInt(s, 10);
}

function renderMathNumber(value) {
  const s = String(value);
  return <InlineMath math={'{' + s + '}'} />;
}

export function generateTask() {
  const n = pick([3, 4, 5, 6, 7, 8, 9, 10]);
  const { s, w, k } = buildFromN(n);

  const givenKey = pick(["s", "k", "w"]);
  const toAskKey = pick(givenKey === "s" ? ["k", "w"] : givenKey === "k" ? ["s", "w"] : ["s", "k"]);

  const givenValue = givenKey === "s" ? s : givenKey === "k" ? k : w;

  let correct;
  if (givenKey === "k" && toAskKey === "w") correct = k / 2 + 1;
  if (givenKey === "k" && toAskKey === "s") correct = k / 2 + 1;
  if (givenKey === "w" && toAskKey === "k") correct = 2 * (w - 1);
  if (givenKey === "w" && toAskKey === "s") correct = w;
  if (givenKey === "s" && toAskKey === "k") correct = 2 * (s - 1);
  if (givenKey === "s" && toAskKey === "w") correct = s;

  const labelMap = { s: "ścian", k: "krawędzi", w: "wierzchołków" };

  return {
    type: "ConvertElementsCountsTask",
    data: {
      n,
      s,
      w,
      k,
      givenKey,
      toAskKey,
      givenValue,
      askLabel: labelMap[toAskKey],
      givenLabel: labelMap[givenKey],
      correct,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      Ostrosłup ma <b>{renderMathNumber(data.givenValue)}</b> {data.givenLabel}. Ile ma {data.askLabel}?
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handle = (ans) => onChange?.(ans);
  return (
    <FractionInput
      value={userAnswer}
      onChange={handle}
      disabled={disabled}
      placeholder="np. 18"
      label=""
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ans = parseIntAnswer(userAnswer);
  if (ans === null) return false;
  return ans === data.correct;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;

  const parsed = parseIntAnswer(userAnswer);
  if (parsed !== null) return renderMathNumber(parsed);

  return <span>{String(userAnswer.raw)}</span>;
}

export function renderAnswer({ data }) {
  return renderMathNumber(data.correct);
}

export function renderExplanation({ data }) {
  const { givenKey, givenValue, toAskKey } = data;

  const general = "s=n+1\\quad w=n+1\\quad k=2n";

  // KROK 2: wyliczamy n z tego, co mamy dane
  let nFormula = "";
  let nCalc = "";
  let nValue = null;

  if (givenKey === "s") {
    nFormula = "n=s-1";
    nValue = givenValue - 1;
    nCalc = `n=${givenValue}-1=${nValue}`;
  } else if (givenKey === "w") {
    nFormula = "n=w-1";
    nValue = givenValue - 1;
    nCalc = `n=${givenValue}-1=${nValue}`;
  } else {
    nFormula = "n=\\tfrac{k}{2}";
    nValue = givenValue / 2;
    nCalc = `n=\\tfrac{${givenValue}}{2}=${nValue}`;
  }

  // KROK 3: z n liczymy szukaną wielkość
  let askFormula = "";
  let askCalc = "";

  if (toAskKey === "s") {
    askFormula = "s=n+1";
    askCalc = `s=${nValue}+1=${nValue + 1}`;
  } else if (toAskKey === "w") {
    askFormula = "w=n+1";
    askCalc = `w=${nValue}+1=${nValue + 1}`;
  } else {
    askFormula = "k=2n";
    askCalc = `k=2\\cdot ${nValue}=${2 * nValue}`;
  }

  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p>
        <strong>Oznaczenia:</strong> <InlineMath math={'{s}'} /> – liczba ścian,{" "}
        <InlineMath math={'{k}'} /> – liczba krawędzi, <InlineMath math={'{w}'} /> – liczba wierzchołków,
        a <InlineMath math={'{n}'} /> – liczba boków wielokąta w podstawie.
      </p>

      <p><strong>Krok 1:</strong> Wzory ogólne dla ostrosłupa o podstawie <InlineMath math={'{n}'} />-kąta:</p>
      <BlockMath math={general} />

      <p><strong>Krok 2:</strong> Najpierw wyznaczamy <InlineMath math={'{n}'} /> z danych:</p>
      <BlockMath math={nFormula} />
      <BlockMath math={nCalc} />

      <p><strong>Krok 3:</strong> Teraz liczymy szukaną wielkość z <InlineMath math={'{n}'} />:</p>
      <BlockMath math={askFormula} />
      <BlockMath math={askCalc} />
    </div>
  );
}
